import { useState } from 'react';

export default function Integration() {
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const featuredIntegrations = [
    { name: 'Salesforce', icon: 'ri-customer-service-2-line', category: 'CRM' },
    { name: 'Slack', icon: 'ri-slack-line', category: 'Communication' },
    { name: 'Google Analytics', icon: 'ri-google-line', category: 'Analytics' },
    { name: 'AWS', icon: 'ri-cloud-line', category: 'Cloud' },
    { name: 'Stripe', icon: 'ri-bank-card-line', category: 'Payment' },
    { name: 'Zapier', icon: 'ri-flashlight-line', category: 'Automation' },
  ];

  const allIntegrations = [
    { name: 'Salesforce', icon: 'ri-customer-service-2-line', category: 'CRM', description: 'Sync customer data and analytics' },
    { name: 'HubSpot', icon: 'ri-customer-service-line', category: 'CRM', description: 'Marketing automation platform' },
    { name: 'Slack', icon: 'ri-slack-line', category: 'Communication', description: 'Team collaboration tool' },
    { name: 'Microsoft Teams', icon: 'ri-microsoft-line', category: 'Communication', description: 'Enterprise communication' },
    { name: 'Google Analytics', icon: 'ri-google-line', category: 'Analytics', description: 'Web analytics service' },
    { name: 'Mixpanel', icon: 'ri-pie-chart-line', category: 'Analytics', description: 'Product analytics platform' },
    { name: 'AWS', icon: 'ri-cloud-line', category: 'Cloud', description: 'Cloud computing services' },
    { name: 'Azure', icon: 'ri-cloud-line', category: 'Cloud', description: 'Microsoft cloud platform' },
    { name: 'Stripe', icon: 'ri-bank-card-line', category: 'Payment', description: 'Online payment processing' },
    { name: 'PayPal', icon: 'ri-paypal-line', category: 'Payment', description: 'Digital payment platform' },
    { name: 'Zapier', icon: 'ri-flashlight-line', category: 'Automation', description: 'Workflow automation' },
    { name: 'Make', icon: 'ri-settings-3-line', category: 'Automation', description: 'Visual automation platform' },
    { name: 'GitHub', icon: 'ri-github-line', category: 'Development', description: 'Code repository hosting' },
    { name: 'GitLab', icon: 'ri-git-repository-line', category: 'Development', description: 'DevOps platform' },
    { name: 'Jira', icon: 'ri-task-line', category: 'Project Management', description: 'Issue tracking software' },
    { name: 'Asana', icon: 'ri-todo-line', category: 'Project Management', description: 'Work management platform' },
    { name: 'Mailchimp', icon: 'ri-mail-send-line', category: 'Marketing', description: 'Email marketing service' },
    { name: 'SendGrid', icon: 'ri-mail-line', category: 'Marketing', description: 'Email delivery platform' },
  ];

  const categories = ['All', 'CRM', 'Communication', 'Analytics', 'Cloud', 'Payment', 'Automation', 'Development', 'Project Management', 'Marketing'];

  const filteredIntegrations = selectedCategory === 'All' 
    ? allIntegrations 
    : allIntegrations.filter(int => int.category === selectedCategory);

  const openModalWithCategory = (category: string) => {
    setSelectedCategory(category);
    setShowModal(true);
  };

  return (
    <section id="integration" className="py-24 bg-gray-950">
      <div className="absolute inset-0 bg-gradient-to-b from-teal-500/5 via-transparent to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-16 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
            <i className="ri-plug-line text-teal-400"></i>
            <span className="text-teal-400 text-sm font-semibold">Integrations</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Connect Your Favorite Tools
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Seamlessly integrate with the tools you already use. Our platform works with over 100+ popular services.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div 
            className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-teal-500/50 transition-all cursor-pointer group text-center"
            onClick={() => openModalWithCategory('CRM')}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg mb-4 mx-auto group-hover:bg-teal-500 transition-colors border border-teal-500/30">
              <i className="ri-customer-service-2-line text-xl text-teal-400 group-hover:text-gray-900 transition-colors"></i>
            </div>
            <h4 className="text-white font-semibold">CRM</h4>
          </div>

          <div 
            className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-teal-500/50 transition-all cursor-pointer group text-center"
            onClick={() => openModalWithCategory('Communication')}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg mb-4 mx-auto group-hover:bg-teal-500 transition-colors border border-teal-500/30">
              <i className="ri-message-3-line text-xl text-teal-400 group-hover:text-gray-900 transition-colors"></i>
            </div>
            <h4 className="text-white font-semibold">Communication</h4>
          </div>

          <div 
            className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-teal-500/50 transition-all cursor-pointer group text-center"
            onClick={() => openModalWithCategory('Analytics')}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg mb-4 mx-auto group-hover:bg-teal-500 transition-colors border border-teal-500/30">
              <i className="ri-bar-chart-box-line text-xl text-teal-400 group-hover:text-gray-900 transition-colors"></i>
            </div>
            <h4 className="text-white font-semibold">Analytics</h4>
          </div>

          <div 
            className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 hover:border-teal-500/50 transition-all cursor-pointer group text-center"
            onClick={() => openModalWithCategory('Cloud')}
          >
            <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg mb-4 mx-auto group-hover:bg-teal-500 transition-colors border border-teal-500/30">
              <i className="ri-cloud-line text-xl text-teal-400 group-hover:text-gray-900 transition-colors"></i>
            </div>
            <h4 className="text-white font-semibold">Cloud</h4>
          </div>
        </div>

        <div className="text-center">
          <button 
            onClick={() => setShowModal(true)}
            className="px-8 py-4 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Integrations
          </button>
        </div>
      </div>

      {/* Integration Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-teal-500/20">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">All Integrations</h3>
                <p className="text-gray-400 text-sm">Connect with your favorite tools</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-400"></i>
              </button>
            </div>

            {/* Category Filters */}
            <div className="p-6 border-b border-gray-800 bg-gray-900/50 sticky top-[88px] z-10">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat
                        ? 'bg-teal-500 text-gray-900'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Integrations Grid */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredIntegrations.map((integration) => (
                  <div
                    key={integration.name}
                    className="bg-gray-800/50 rounded-xl p-5 border border-gray-700 hover:border-teal-500/50 transition-all group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-teal-500/20 rounded-lg group-hover:bg-teal-500 transition-colors border border-teal-500/30">
                        <i className={`${integration.icon} text-xl text-teal-400 group-hover:text-gray-900 transition-colors`}></i>
                      </div>
                      <div className="flex-1">
                        <p 
                          onClick={() => setSelectedCategory(integration.category)}
                          className="text-teal-400 text-xs mb-2 cursor-pointer hover:text-teal-300 transition-colors"
                        >
                          {integration.category}
                        </p>
                        <h4 className="text-white font-semibold mb-1">{integration.name}</h4>
                        <p className="text-gray-400 text-sm">{integration.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-800 bg-gray-900 sticky bottom-0">
              <button
                onClick={() => setShowModal(false)}
                className="w-full px-6 py-3 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}